import React, { useRef, useState } from 'react';
import '../styles/PropertyCard.css';
import { Bathroom, Bedroom, House, Image, LotSize, MapBubble, SquareFootage, YearBuilt } from '../assets/icons';
import { colour, formatterLong, numberFormatter, percentageCompact, percentage, roundToAppropriateDecimals, formatterCompact } from '../styles/GlobalStyles';
import { Badge, Button, Ceiling, CompCardMap, Condition, CustomSwiper, Disqualifier, Distance, NovationTimeline, PropertyIcon, PropertyLinks } from './';
import { formatDaysOnMarket, getFlipDetails, getPriceField, getStatusDotColour, propertyConditionBG, propertyStatusBG, propertyStatusText, propertyTypeFormat, squareFeetToAcres } from '../functions';
import { Calendar03Icon, PoolIcon, CarParking01Icon } from 'hugeicons-react';

function PropertyCard(props) {
    const property = props.property;
    const subjectProperty = props.subjectProperty;
    const subjectPropertyObject = props.subjectPropertyObject;
    const propertyType = property.propertyAttributes.propertyType;
    const showScrollableImages = props.showScrollableImages;
    const testView = props.testView;
    const selected = props.selected;
    const toggleSelectedComp = props.toggleSelectedComp;
    const bestComp = props.bestComp;
    const openDetailedView = props.openDetailedView;
    const clickedZPID = props.clickedZPID;
    const clickedCompDescription = props.clickedCompDescription;
    const onChangeZPIDHover = props.onChangeZPIDHover;
    const readOnly = props.readOnly;
    const isLoaded = props.isLoaded;
    const innerWidth = props.innerWidth;
    const gptLoading = props.gptLoading;
    const priceCeiling = props.priceCeiling;
    const priceCeilingText = props.priceCeilingText;
    const darkMode = props.darkMode;
    const conditionUpdate = props.conditionUpdate;
    const finalCondition = subjectProperty === true ? null : conditionUpdate.findIndex(e => e.zpid === property.uniqueIds.zillowId) !== -1 ? conditionUpdate[conditionUpdate.findIndex(e => e.zpid === property.uniqueIds.zillowId)].condition : property.compDetails.propertyCondition;
    const flipDetails = subjectProperty === true ? null : getFlipDetails(property);
    const flipStatus = flipDetails === null ? null : flipDetails.flipStatus;
    const flipBGColour = flipDetails === null ? null : darkMode === true ? flipDetails.flipBGColourDark : flipDetails.flipBGColour;
    const flipDescription = flipDetails === null ? null : flipDetails.flipDescription;
    const flipDotcolour = flipDetails === null ? null : flipDetails.flipDotcolour;
    const cardRef = useRef();
    const [showMap, setShowMap] = useState(false);
    const [distanceHoverTimeout, setDistanceHoverTimeout] = useState(null);

    const details = [
        {
            label: propertyTypeFormat(propertyType),
            bold: null,
            icon: <PropertyIcon propertyType={propertyType} fill={colour.gray04} className="" size={14} />,
            difference: null
        },
        {
            label: `${property.propertyAttributes.bedrooms} beds`,
            bold: null,
            icon: <Bedroom className="" size={14} fill={colour.gray04} />,
            difference: subjectProperty === true ? null : property.propertyAttributes.bedrooms - subjectPropertyObject.propertyAttributes.bedrooms,
            differenceNumber: subjectProperty === true ? null : property.propertyAttributes.bedrooms - subjectPropertyObject.propertyAttributes.bedrooms
        },
        {
            label: `${property.propertyAttributes.bathrooms} baths`,
            bold: null,
            icon: <Bathroom className="" size={14} fill={colour.gray04} />,
            difference: subjectProperty === true ? null : roundToAppropriateDecimals(property.propertyAttributes.bathrooms - subjectPropertyObject.propertyAttributes.bathrooms),
            differenceNumber: subjectProperty === true ? null : roundToAppropriateDecimals(property.propertyAttributes.bathrooms - subjectPropertyObject.propertyAttributes.bathrooms)
        },
        {
            label: `${numberFormatter.format(property.propertyAttributes.livingSquareFootage)} sqft`,
            bold: null,
            icon: <SquareFootage fill={colour.gray04} className="" />,
            difference: subjectProperty === true ? null : percentageCompact.format(((property.propertyAttributes.livingSquareFootage - subjectPropertyObject.propertyAttributes.livingSquareFootage) / subjectPropertyObject.propertyAttributes.livingSquareFootage)),
            differenceNumber: subjectProperty === true ? null : property.propertyAttributes.livingSquareFootage - subjectPropertyObject.propertyAttributes.livingSquareFootage
        },
        {
            label: `Built ${property.propertyAttributes.yearBuilt}`,
            bold: null,
            icon: <YearBuilt fill={colour.gray04} className="" />,
            difference: subjectProperty === true ? null : `${property.propertyAttributes.yearBuilt - subjectPropertyObject.propertyAttributes.yearBuilt} yr`,
            differenceNumber: subjectProperty === true ? null : property.propertyAttributes.yearBuilt - subjectPropertyObject.propertyAttributes.yearBuilt
        },
        {
            label: `${squareFeetToAcres(property.propertyAttributes.lotSquareFootage)}`,
            bold: null,
            icon: <LotSize fill={colour.gray04} className="" />,
            difference: subjectProperty === true || subjectPropertyObject.propertyAttributes.lotSquareFootage === null || property.propertyAttributes.lotSquareFootage === null ? null : percentageCompact.format(((property.propertyAttributes.lotSquareFootage - subjectPropertyObject.propertyAttributes.lotSquareFootage) / subjectPropertyObject.propertyAttributes.lotSquareFootage)),
            differenceNumber: subjectProperty === true || subjectPropertyObject.propertyAttributes.lotSquareFootage === null || property.propertyAttributes.lotSquareFootage === null ? null : property.propertyAttributes.lotSquareFootage - subjectPropertyObject.propertyAttributes.lotSquareFootage
        },
        {
            label: property.propertyAttributes.garage === true ? "Garage" : null,
            bold: null,
            icon: <CarParking01Icon size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: property.propertyAttributes.pool === true ? "Pool" : null,
            bold: null,
            icon: <PoolIcon size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: subjectProperty === true ? null : property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? "Listed" : "Sold",
            bold: subjectProperty === true ? null : property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? formatDaysOnMarket(property.listingDetails.saleListingDate) : formatDaysOnMarket(property.listingDetails.soldListingDate),
            icon: subjectProperty === true ? null : <Calendar03Icon color={colour.gray04} className="" size={16} />,
            difference: null,
            differenceNumber: null
        }
    ];

    const turnOnMap = () => {
        setShowMap(true);
    };

    const turnOffMap = () => {
        setShowMap(false);
        setDistanceHoverTimeout(null);
    };

    return (
        <div 
            className={"property-card-container " + (darkMode === true ? "bg-colour-dark-mode " : subjectProperty === true ? "bg-colour-x-light " : "bg-colour-white property-card-border ") + (subjectProperty === false && clickedZPID === property.uniqueIds.zillowId ? "property-card-outer-border " : "")}
            ref={cardRef}
            onMouseEnter={() => onChangeZPIDHover === null ? null : onChangeZPIDHover(property.uniqueIds.zillowId)}
            onMouseLeave={() => onChangeZPIDHover === null ? null : onChangeZPIDHover(null)}
        >
            <div className="property-card-inner-container">
                {
                    testView === true &&
                    <div className="property-card-test-view-container">
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Overall = {percentage.format(property.compDetails.overallScore)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Location = {percentage.format(property.compDetails.locationScore)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Recency = {percentage.format(property.compDetails.recencyScore)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Similarity = {percentage.format(property.compDetails.similarityScore)}
                            </span>
                        }
                        <span className="body-regular colour-primary">
                            Subdivision = {property.propertyAttributes.subdivision}
                        </span>
                        <span className="body-regular colour-primary">
                            Census block = {property.addressDetails.censusBlock}
                        </span>
                        <span className="body-regular colour-primary">
                            Geo ID = {property.addressDetails.geoId}
                        </span>
                        <span className="body-regular colour-primary">
                            Median home = {formatterCompact.format(property.economicData.tract.median_home_value)}
                        </span>
                        <span className="body-regular colour-primary">
                            Median income = {formatterCompact.format(property.economicData.tract.median_household_income)}
                        </span>
                        <span className="body-regular colour-primary">
                            Construction = {property.propertyAttributes.constructionTypeBySource.reapi === null ? property.propertyAttributes.constructionTypeBySource.zillow : property.propertyAttributes.constructionTypeBySource.reapi}
                        </span>
                        <span className="body-regular colour-primary">
                            Waterfront = {JSON.stringify(property.propertyAttributes.waterFront)}
                        </span>
                    </div>
                }
                <div 
                    className="property-card-image-container relative-container"
                    onMouseLeave={() => turnOffMap()}
                >
                    {
                        subjectProperty === false && property.compDetails.distanceMiles !== null &&
                        <Distance
                            value={`${property.compDetails.distanceMiles.toFixed(2)}mi`}
                            distanceHover={() => turnOnMap()}
                            showMap={showMap}
                            hoverTimeout={distanceHoverTimeout}
                            setHoverTimeout={setDistanceHoverTimeout}
                            darkMode={darkMode}
                        />
                    }
                    {
                        showMap === true && subjectProperty === false ?
                        <CompCardMap
                            isMapLoaded={isLoaded}
                            comp={property}
                            subjectProperty={subjectPropertyObject}
                            isMapActive={true}
                            bestComp={bestComp}
                        />
                        :
                        property.listingDetails.mlsPhotoUrls === null || property.listingDetails.mlsPhotoUrls.length === 0 ?
                        <div 
                            className="property-card-no-image-container"
                        >
                            <Image
                                className="property-card-no-image-element"
                                fill={colour.gray05}
                            />
                            <span className="label-regular colour-secondary block-text text-align-center">
                                No image
                            </span>
                        </div>
                        :
                        property.listingDetails.mlsPhotoUrls.length === 1 ?
                        <div 
                            className="property-card-single-image-container"
                            style={{ backgroundImage: `url(${property.listingDetails.mlsPhotoUrls[0]})` }}
                        >
                        </div>
                        :
                        <CustomSwiper
                            images={property.listingDetails.mlsPhotoUrls}
                            imageLimit={100}
                            title=""
                            arrowClicked={null}
                            imageClick={() => showScrollableImages === null ? null : showScrollableImages(property)}
                        />
                    }
                    {
                        subjectProperty === false && selected === false && readOnly === false ?
                        <Button
                            buttonType="primary-button"
                            size="medium"
                            text={"Keep"}
                            cta={toggleSelectedComp}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                        :
                        subjectProperty === false && selected === true && readOnly === false ?
                        <Button
                            buttonType="light-button"
                            size="medium"
                            text={"Remove"}
                            cta={toggleSelectedComp}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                        :
                        null
                    }
                </div>
                <div 
                    className={"property-card-body-container " + (subjectProperty === true ? "" : "cursor-pointer")}
                    onClick={() => subjectProperty === true ? null : openDetailedView === null ? null : openDetailedView(property)}
                >
                    {
                        priceCeiling === true &&
                        <Ceiling />
                    }
                    <div className="property-card-details-container">
                        {
                            subjectProperty === true ?
                            <div className="run-comps-subject-property-title-container desktop-none">
                                <div className="relative-container">
                                    <MapBubble
                                        className=""
                                        fill={colour.gray07}
                                        stroke={colour.gray00}
                                        strokeWidth="0.5"
                                    />
                                    <House
                                        fill={colour.gray00}
                                        className="all-comps-map-house-icon"
                                    />
                                </div>
                                <span className="label-regular-caps colour-primary">
                                    Subject property
                                </span>
                            </div>
                            :
                            bestComp === true ?
                            <div className="run-comps-subject-property-title-container desktop-none">
                                <div className="relative-container">
                                    <MapBubble
                                        className=""
                                        fill={colour.jade01}
                                        stroke={colour.jade02}
                                        strokeWidth="0.5"
                                    />
                                    <House
                                        fill={colour.jade03}
                                        className="all-comps-map-house-icon"
                                    />
                                </div>
                                <span className="label-regular-caps colour-primary">
                                    Suggested comp
                                </span>
                            </div>
                            :
                            null
                        }
                        <div className="property-card-price-container">
                            <h3 className={"heading-small-bold " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                {
                                    subjectProperty === true ?
                                    "N/A"
                                    :
                                    getPriceField(property) === null ? 
                                    "Price unavailable"
                                    :
                                    formatterLong.format(getPriceField(property)).replace(".00", "")
                                }
                            </h3>
                            {
                                property.mlsVerified === true &&
                                 <Badge
                                    bgColour={"bg-colour-flip"}
                                    text={"MLS VERIFIED"}
                                    statusDot={true}
                                    statusDotColour={colour.jade02}
                                    textClass="body-semibold colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={false}
                                />
                            }
                        </div>
                        <span className={"body-regular block-text white-space-no-wrap " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                            {property.addressDetails.streetAddress}
                        </span>
                        {
                            subjectProperty === false ?
                            <Condition
                                label={finalCondition}
                                iconBG={propertyConditionBG(finalCondition, darkMode)}
                                darkMode={darkMode}
                            />
                            :
                            <PropertyLinks
                                property={property}
                                darkMode={darkMode}
                            />
                        }
                    </div>
                    <div className="property-card-details-column">
                        {
                            details.map((item, index) =>
                                item.label !== null &&
                                <div 
                                    className="property-card-detail-element"
                                    key={index}
                                >
                                    {item.icon}
                                    <span className={"body-regular " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                        {item.label}
                                        {
                                            item.bold !== null &&
                                            <span className={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                {" "}{item.bold}
                                            </span>
                                        }
                                    </span>
                                    {
                                        item.differenceNumber === null || item.differenceNumber === 0 || item.difference === "0%" || item.difference === "-0%" ?
                                        null
                                        :
                                        index === 4 ?
                                        <span className={"body-semibold " + (Math.abs(item.differenceNumber) >= 20 ? (darkMode === true ? "colour-red-02" : "colour-error") : Math.abs(item.differenceNumber) >= 10 ? "colour-yellow" : (darkMode === true ? "colour-green-02" : "colour-green"))}>
                                            {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                        </span>
                                        :
                                        <span className={"body-semibold " + (item.differenceNumber > 0 ? (darkMode === true ? "colour-green-02" : "colour-green") : (darkMode === true ? "colour-red-02" : "colour-error"))}>
                                            {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                        </span>
                                    }
                                </div>
                            )
                        }
                    </div>
                    {
                        subjectProperty === false &&
                        <div className="property-card-badges-row">
                            <Badge
                                bgColour={propertyStatusBG(property, darkMode)}
                                text={propertyStatusText(property)}
                                statusDot={true}
                                statusDotColour={getStatusDotColour(property)}
                                textClass={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={false}
                            />
                            <div className="relative-container">
                                <Badge
                                    bgColour={flipBGColour}
                                    text={flipStatus}
                                    statusDot={true}
                                    statusDotColour={flipDotcolour}
                                    textClass={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}
                                    icon={null}
                                    tooltip={true}
                                    tooltipBody={flipDescription}
                                    tooltipPosition={"top"}
                                    iconLeft={false}
                                />
                            </div>
                        </div>
                    }
                    {
                        subjectProperty === false && property.novationTimeline !== undefined && property.novationTimeline !== null && property.novationTimeline.length > 0 &&
                        <NovationTimeline
                            property={property}
                            innerWidth={innerWidth}
                            darkMode={darkMode}
                        />
                    }
                </div>
                {
                    priceCeiling === true && priceCeilingText !== null ?
                    <div 
                        className="property-card-disqualifier-container cursor-pointer"
                        onClick={() => openDetailedView === null ? null : openDetailedView(property)}
                    >
                        <Disqualifier
                            text={`**Price ceiling** - ${priceCeilingText}`}
                            loading={false}
                            key={priceCeilingText}
                        />
                    </div>
                    :
                    bestComp === false && subjectProperty === false && (clickedCompDescription !== null || gptLoading === true) ?
                    <div 
                        className="property-card-disqualifier-container cursor-pointer"
                        onClick={() => openDetailedView === null ? null : openDetailedView(property)}
                    >
                        <Disqualifier
                            text={clickedCompDescription}
                            loading={gptLoading}
                            key={clickedCompDescription}
                        />
                    </div>
                    :
                    null
                }
            </div>
        </div>
    )
};

export default PropertyCard;